import Downshift, { ControllerStateAndHelpers, StateChangeOptions } from 'downshift';
import React, { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import ICONS from '../constants/icons';
import { COLORS } from '../constants/styles';
import { FieldHelpers } from '../fields/fieldHelpers';
import Icon from './Icon';
import InputField from './InputField';
import { IErrorMessage } from './errorMessage/errorMessage.type';
import { TickIcon } from './icons/TickIcon';

interface ISearchSelectProps<T, K> {
    autoComplete?: string;
    autoCompleteSearchKeys?: Array<keyof T>;
    addText?: string;
    id: string;
    items: T[];
    label?: FormattedMessage.MessageDescriptor | string;
    error?: IErrorMessage;
    searchKey: K;
    onAddClick?: () => any;
    onSelect: (item: T) => void;
    required?: boolean;
    setTouched?: (id: string, isTouched: boolean) => any;
    selectedItem?: T;
    dummySelectedText?: string;
    disabled?: boolean;
    placeholder?: string;
    qaId?: string;
    selectedItemKey?: keyof T;
    inputDisabled?: boolean;
    showAddressForm?: boolean;
}

interface ISearchSelectState<T> {
    filteredItems: T[];
    selectedItem: T | undefined;
    disableAutoComplete: boolean;
    searchedText: string | null;
}

const AddButton = styled.button`
    background: none;
    border: none;
    border-radius: 5px;
    color: ${COLORS.rakutenRed};
    cursor: pointer;
    padding: 10px;
    text-align: left;
    width: 100%;
`;

export const DropdownWrapper = styled.div`
    position: relative;
`;

export const List = styled.ul`
    background-color: ${COLORS.white};
    border-radius: 5px;
    box-shadow: 0 2px 5px 0 ${COLORS.lightGrey};
    list-style: none;
    margin: 0;
    top: 2.5rem;
    max-height: 250px;
    overflow-x: hidden;
    overflow-y: auto;
    padding: 0;
    position: absolute;
    width: 100%;
    z-index: 20000;
`;

interface IItemProps {
    highlighted: boolean;
}

export const Item = styled.li`
    display: flex;
    background-color: ${(props: IItemProps) =>
        props.highlighted ? COLORS.lightGrey : 'transparent'};
    cursor: pointer;
    padding: 10px;

    &:hover {
        background-color: ${COLORS.ultralightGrey};
    }
`;
const ItemSearchValue = styled.div`
    flex: 1;
`;

const ItemSearchValueSelected = styled.div`
    flex: 1;
    font-weight: 800;
`;

interface IArrowIconProps {
    hasValue: boolean;
    disabled?: boolean;
}

const ArrowIcon = styled.span`
    color: ${(props: IArrowIconProps) => (props.hasValue ? COLORS.dark : COLORS.grey)};
    display: ${(props: IArrowIconProps) => (props.disabled ? 'none' : 'inline-block')};
    margin-left: 5px;
    position: absolute;
    right: 0.625rem;
    top: 0.6rem;
    z-index: 0;
`;
const SearchInputField = styled(InputField)`
    z-index: 1;
`;

export default class SearchSelect<T, K extends keyof T> extends React.Component<
    ISearchSelectProps<T, K>,
    ISearchSelectState<T>
> {
    constructor(props: ISearchSelectProps<T, K>) {
        super(props);

        const { items, selectedItem } = this.props;

        this.state = {
            disableAutoComplete: false,
            filteredItems: [...items],
            selectedItem,
            searchedText: null,
        };

        this.filter = this.filter.bind(this);
        this.onItemSelect = this.onItemSelect.bind(this);
        this.onDfStateChange = this.onDfStateChange.bind(this);
        this.renderSearchSelect = this.renderSearchSelect.bind(this);
        this.getSearchValue = this.getSearchValue.bind(this);
    }
    static getDerivedStateFromProps(nextProps: any, prevState: any) {
        return Object.assign({}, prevState, {
            selectedItem: nextProps.selectedItem,
        });
    }

    public render() {
        const { selectedItem } = this.state;

        return (
            <Downshift
                initialSelectedItem={selectedItem}
                onSelect={this.onItemSelect}
                onInputValueChange={this.filter}
                onStateChange={this.onDfStateChange}
                children={this.renderSearchSelect}
                itemToString={this.getSearchValue}
            />
        );
    }

    private generateItemValue(
        item: T,
        selectedItem: T | undefined,
        selectedItemKey: keyof T | undefined,
        itemSearchValue: string,
    ): ReactNode {
        if (
            selectedItem &&
            selectedItem !== undefined &&
            selectedItemKey !== undefined &&
            selectedItem[selectedItemKey] !== undefined &&
            selectedItem[selectedItemKey] === item[selectedItemKey]
        ) {
            return (
                <>
                    <ItemSearchValueSelected>{itemSearchValue}</ItemSearchValueSelected>
                    <TickIcon />
                </>
            );
        } else {
            return <ItemSearchValue>{itemSearchValue}</ItemSearchValue>;
        }
    }

    private renderSearchSelect({
        closeMenu,
        isOpen,
        toggleMenu,
        getRootProps,
        getInputProps,
        getItemProps,
        highlightedIndex,
    }: ControllerStateAndHelpers<T>): ReactNode {
        const {
            autoComplete,
            disabled,
            label,
            id,
            setTouched,
            required,
            placeholder,
            dummySelectedText,
            qaId,
            selectedItemKey,
            inputDisabled,
            showAddressForm,
        } = this.props;
        const { filteredItems, selectedItem, disableAutoComplete, searchedText } = this.state;

        const listItems = filteredItems.map((item, index) => {
            const itemSearchValue = this.getSearchValue(item);
            const itemValue = this.generateItemValue(
                item,
                selectedItem,
                selectedItemKey,
                itemSearchValue,
            );

            return (
                <Item
                    {...getItemProps({ item })}
                    data-qa-id={`dropdown-${qaId}-${index}`}
                    key={index}
                    highlighted={highlightedIndex === index}
                >
                    {itemValue}
                </Item>
            );
        });
        const addButtonItem = this.renderAddButton(
            highlightedIndex === listItems.length,
            closeMenu,
        );
        const list = isOpen ? (
            <List tabIndex={-1}>
                {listItems}
                {addButtonItem}
            </List>
        ) : (
            ''
        );
        const { error } = this.props;
        const onFocus = (() => {
            this.resetFiltered();
            if (setTouched) {
                setTouched(id, true);
            }
            const inputEl = document.getElementById(id) as HTMLInputElement | null;
            if (inputEl) {
                FieldHelpers.disableAutoFill(inputEl);
                inputEl.select();
            }
            toggleMenu();
        }).bind(this);
        const onBlur = () => {
            const inputEl = document.getElementById(id) as HTMLInputElement | null;
            if (inputEl) {
                FieldHelpers.enableAutoFill(inputEl, id, autoComplete ? autoComplete : 'off');
            }
        };
        const itemSearchValue = this.getSearchValue(selectedItem);
        let textInInput;
        if (showAddressForm) {
            textInInput = showAddressForm ? dummySelectedText : itemSearchValue;
        } else {
            textInInput =
                searchedText == null
                    ? dummySelectedText
                        ? dummySelectedText
                        : itemSearchValue
                    : searchedText;
        }

        const displayTextInput = inputDisabled
            ? textInInput !== itemSearchValue
                ? itemSearchValue
                : textInInput
            : textInInput;
        return (
            <DropdownWrapper
                {...getRootProps({ refKey: 'innerRef' })}
                hasValue={selectedItem}
                onBlur={onBlur}
            >
                <SearchInputField
                    {...getInputProps()}
                    aria-autocomplete={disableAutoComplete ? 'none' : undefined}
                    autoComplete={disableAutoComplete ? 'off' : autoComplete}
                    labelMessage={label}
                    type="search"
                    id={id}
                    error={error}
                    required={required}
                    onFocus={onFocus}
                    disabled={disabled}
                    placeholder={placeholder}
                    value={displayTextInput || ''}
                    data-qa-id={`dropdown-input-${qaId}`}
                />
                <ArrowIcon hasValue={Boolean(isOpen || selectedItem)} disabled={disabled}>
                    <Icon icon={ICONS.ARROW_DOWN} size="20" viewBoxMax="20" />
                </ArrowIcon>
                {list}
            </DropdownWrapper>
        );
    }

    private onDfStateChange(
        changes: StateChangeOptions<T>,
        stateAndHelpers: ControllerStateAndHelpers<T>,
    ) {
        const { autoCompleteSearchKeys, items } = this.props;
        const { filteredItems, selectedItem } = this.state;

        if (
            !selectedItem &&
            changes.type === Downshift.stateChangeTypes.changeInput &&
            changes.inputValue &&
            filteredItems.length === 1 &&
            0.75 * this.getSearchValue(filteredItems[0]).length <= changes.inputValue.length
        ) {
            // Autocomplete if filtered items already has only one possible element
            // and more than 75% of value has been typed
            const autoFilledItem = filteredItems[0];
            stateAndHelpers.selectItem(autoFilledItem);
            this.onItemSelect(autoFilledItem);
        }

        if (
            autoCompleteSearchKeys &&
            changes.type === Downshift.stateChangeTypes.changeInput &&
            changes.inputValue &&
            filteredItems.length === 0
        ) {
            // Check other keys of the item for the value, e.g. country code and name for country
            // This is important for auto-fill
            const autoFilledItem = items.find((item: T) => {
                const matchingKeys = autoCompleteSearchKeys.filter((key: keyof T) => {
                    return (
                        changes.inputValue &&
                        item &&
                        item[key] &&
                        String(item[key]).toLowerCase() === changes.inputValue.toLowerCase()
                    );
                });
                return matchingKeys.length > 0;
            });
            if (autoFilledItem) {
                stateAndHelpers.selectItem(autoFilledItem);
                this.onItemSelect(autoFilledItem);
            }
        }
    }

    private renderAddButton(highlighted: boolean, closeMenu: () => void) {
        const { addText, onAddClick, qaId } = this.props;
        const onMouseDown = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            event.preventDefault();
            if (onAddClick) {
                onAddClick();
            }
            closeMenu();
        };

        return addText ? (
            <Item key="add" highlighted={highlighted}>
                <AddButton onMouseDown={onMouseDown} data-qa-id={`dropdown-add-button-${qaId}`}>
                    {addText}
                </AddButton>
            </Item>
        ) : (
            ''
        );
    }

    private onItemSelect(selectedItem: T) {
        const { onSelect } = this.props;
        onSelect(selectedItem);
        this.resetFiltered();
    }

    private filter(inputValue: string, stateAndHelpers: ControllerStateAndHelpers<T>) {
        const { searchKey, items, inputDisabled } = this.props;
        const { selectedItem } = this.state;

        if (inputDisabled) {
            return;
        }

        if (this.getSearchValue(selectedItem) !== inputValue.toLowerCase()) {
            this.setState({
                filteredItems: items.filter((item: T) => {
                    const itemSearchValue = this.getSearchValue(item);
                    const regex = `(^|(\\s+))${inputValue.toLowerCase()}`;
                    return !inputValue || itemSearchValue.toLowerCase().match(regex);
                }),
                searchedText: inputValue,
            });
        }
        this.setState({ searchedText: inputValue });
    }

    private resetFiltered() {
        const { items } = this.props;
        this.setState({
            filteredItems: [...items],
        });
    }

    private getSearchValue(item: T | undefined): string {
        const { searchKey } = this.props;
        const key = item && item[searchKey] ? String(item[searchKey]) : '';
        return key;
    }
}
